import { ulid } from '@w/utils'
import { UAParser } from 'ua-parser-js'

type BrowserUserAgentData = {
  mobile: boolean
  platform: string
  getHighEntropyValues?: (
    hints: Array<
      | 'architecture'
      | 'bitness'
      | 'model'
      | 'platformVersion'
      | 'fullVersionList'
    >,
  ) => Promise<{
    architecture?: string
    bitness?: string
    model?: string
    platformVersion?: string
    fullVersionList?: Array<{ brand: string; version: string }>
  }>
}

// A storage that refuses reads or writes (blocked site data, full quota) yields a fresh id for this load.
export async function getBrowserDeviceId(storage: Storage): Promise<string> {
  const existing = readDeviceId(storage)
  if (existing) return existing

  const prefix = await getBrowserDevicePrefix()
  // Another caller may have initialized storage while client hints resolved.
  const initialized = readDeviceId(storage)
  if (initialized) return initialized

  const next = `${prefix}-${ulid()}`
  try {
    storage.setItem('browser-device-id', next)
  } catch {
    // Not persisted: the next load mints another id.
  }
  return next
}

function readDeviceId(storage: Storage): string | null {
  try {
    return storage.getItem('browser-device-id')
  } catch {
    return null
  }
}

export async function getBrowserDevicePrefix(
  userAgent: string = globalThis.navigator?.userAgent ?? '',
  userAgentData: BrowserUserAgentData | undefined = readDefaultUserAgentData(
    userAgent,
  ),
): Promise<string> {
  const clientHints = await readClientHints(userAgentData)
  if (clientHints) return clientHints

  const result = UAParser(userAgent)
  const browser = slug(
    (result.browser.name ?? 'browser').replace(/^Mobile /, ''),
  )
  const os = slug(result.os.name ?? 'unknown-os')
  const deviceType = slug(result.device.type ?? 'desktop')
  const deviceModel =
    deviceType === 'desktop' ? '' : slug(result.device.model ?? '')
  return [browser, deviceType, os, deviceModel].filter(Boolean).join('-')
}

function readDefaultUserAgentData(
  userAgent: string,
): BrowserUserAgentData | undefined {
  if (userAgent !== globalThis.navigator?.userAgent) return undefined
  return (
    globalThis.navigator as Navigator & {
      userAgentData?: BrowserUserAgentData
    }
  )?.userAgentData
}

async function readClientHints(
  userAgentData: BrowserUserAgentData | undefined,
): Promise<string | null> {
  if (!userAgentData?.getHighEntropyValues) return null
  const values = await userAgentData.getHighEntropyValues([
    'architecture',
    'bitness',
    'model',
    'platformVersion',
    'fullVersionList',
  ])
  const browser = readBrowserBrand(values.fullVersionList)
  const deviceType = userAgentData.mobile ? 'mobile' : 'desktop'
  const platform = slug(userAgentData.platform || 'unknown-os')
  const architecture = slug(values.architecture ?? '')
  const bitness = slug(values.bitness ?? '')
  const model = slug(values.model ?? '')
  const majorPlatformVersion = slug(
    values.platformVersion?.split('.').at(0) ?? '',
  )
  return [
    browser,
    deviceType,
    platform,
    architecture,
    bitness,
    model,
    majorPlatformVersion ? `v${majorPlatformVersion}` : '',
  ]
    .filter(Boolean)
    .join('-')
}

function readBrowserBrand(
  fullVersionList: Array<{ brand: string; version: string }> | undefined,
): string {
  const brand =
    fullVersionList?.find((item) => !item.brand.includes('Not'))?.brand ??
    'browser'
  return slug(brand.replace(/^Google /, ''))
}

function slug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replaceAll(/^-|-$/g, '')
}
